import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Button, Pagination } from 'antd';
import { FireOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import auctionService from '../services/auctionService';
import { toggleFavoriteAuction } from '../redux/userSlice';
import AuctionCard from '../components/Auction/AuctionCard';
import AuctionCardSkeleton from '../components/Auction/AuctionCardSkeleton';
import AuctionCountdown from '../components/Common/AuctionCountdown';
import '../components/Auction/AuctionList.css';
import './FeaturedPages.css';

const { Title, Text } = Typography;

const FeaturedAuctionsPage = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        loadFeaturedAuctions();
    }, [pagination.current, pagination.pageSize]);

    // Sync local state with Redux favorites
    useEffect(() => {
        if (currentUser) {
            const favoriteAuctions = currentUser.favorite_auctions || [];
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }))
            );
        }
    }, [currentUser?.favorite_auctions]);

    const loadFeaturedAuctions = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getAllAuctions({
                featured: true,
                page: pagination.current,
                per_page: pagination.pageSize
            });
            
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = (response.data || []).map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));
            
            setAuctions(auctionsWithFavorites);
            
            if (response.meta) {
                setPagination(prev => ({
                    ...prev,
                    current: response.meta.current_page || prev.current,
                    total: response.meta.total || 0,
                    pageSize: response.meta.per_page || prev.pageSize
                }));
            }
        } catch (error) {
            console.error('Error loading featured auctions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePaginationChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFavoriteToggle = (auctionId) => {
        // Optimistically update local state
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));
    };

    return (
        <div className="featured-page-container">
            {/* Professional Header */}
            <div className="featured-page-header">
                {/* Background decoration */}
                <div className="featured-page-header-decoration" />
                
                <div className="featured-page-header-content">
                    <div className="featured-page-header-title-wrapper">
                        <FireOutlined className="featured-page-header-icon" />
                        <Title level={1} className="featured-page-header-title">
                            All Featured Auctions
                        </Title>
                    </div>
                    <Text className="featured-page-header-description">
                        Discover our handpicked selection of premium auctions from top sellers
                    </Text>
                </div>
            </div>

            {loading ? (
                <Row gutter={[24, 24]}>
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                        <Col xs={24} sm={12} md={8} lg={8} key={item}>
                            <AuctionCardSkeleton viewMode="grid" />
                        </Col>
                    ))}
                </Row>
            ) : auctions.length === 0 ? (
                <div className="empty-container">
                    <div className="empty-card">
                        <div style={{ 
                            fontSize: '64px', 
                            marginBottom: '24px',
                            opacity: 0.3
                        }}>
                            🏆
                        </div>
                        <Text className="empty-title">
                            No featured auctions found
                        </Text>
                        <Text className="empty-description">
                            No featured auctions are currently available. Check back later for new featured auctions.
                        </Text>
                        <div style={{ marginTop: '24px' }}>
                            <Button 
                                type="primary" 
                                onClick={() => window.location.reload()}
                                style={{
                                    borderRadius: '8px',
                                    height: '40px',
                                    padding: '0 24px',
                                    fontWeight: 500
                                }}
                            >
                                Refresh Page
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <Row gutter={[24, 24]} className="featured-page-grid">
                        {auctions.map(auction => (
                            <Col xs={24} sm={12} md={8} lg={8} key={auction.id} className="featured-page-col">
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <AuctionCard
                                        auction={auction}
                                        viewMode="grid"
                                        onFavoriteToggle={handleFavoriteToggle}
                                    />
                                    {/* Real-time countdown overlay */}
                                    <AuctionCountdown
                                        auction={auction}
                                        variant="overlay"
                                    />
                                </div>
                            </Col>
                        ))}
                    </Row>
                    
                    {/* Pagination */}
                    {pagination.total > 0 && (
                        <div className="featured-page-pagination">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={handlePaginationChange}
                                showSizeChanger={false}
                                responsive={true}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FeaturedAuctionsPage;

